import { defineMessages, useIntl } from "react-intl";
import { TestType } from "@app/provider/testData/types";

export type QA = {
  question: string;
  answer: string;
};

export type FAQSection = {
  title: string;
  content: QA[];
};

export type FAQContent = {
  preTest: FAQSection[];
  postTest?: FAQSection[];
};

const messages = defineMessages({
  // Pre-Test: Purpose of the Test
  preTestPurposeTitle: {
    id: "patientEducation.faq.signatera.preTest.purposeOfTest.title",
    defaultMessage: "Purpose of the Test",
  },
  preTestPurposeQ1Question: {
    id: "patientEducation.faq.signatera.preTest.purposeOfTest.q1Question",
    defaultMessage:
      "What is the Signatera™ test, and what is it designed to detect in my blood?",
  },
  preTestPurposeQ1Answer: {
    id: "patientEducation.faq.signatera.preTest.purposeOfTest.q1Answer",
    defaultMessage:
      "Signatera™ is a personalized blood test that looks for tumor-specific circulating tumor DNA (ctDNA). It is designed to help detect minimal residual disease (MRD) or early signs that cancer may be returning, sometimes before symptoms or imaging changes appear.",
  },
  preTestPurposeQ2Question: {
    id: "patientEducation.faq.signatera.preTest.purposeOfTest.q2Question",
    defaultMessage: "Why might my doctor recommend Signatera™ testing?",
  },
  preTestPurposeQ2Answer: {
    id: "patientEducation.faq.signatera.preTest.purposeOfTest.q2Answer",
    defaultMessage:
      "Your doctor may recommend Signatera™ to check for remaining cancer after treatment, track how cancer is responding, or watch for early signs of recurrence. The test provides additional information and is used together with scans and other clinical evaluations.",
  },

  // Pre-Test: Personalization
  preTestPersonalizationTitle: {
    id: "patientEducation.faq.signatera.preTest.personalization.title",
    defaultMessage: "Personalization",
  },
  preTestPersonalizationQ1Question: {
    id: "patientEducation.faq.signatera.preTest.personalization.q1Question",
    defaultMessage:
      "How is Signatera™ customized to my specific cancer or tumor?",
  },
  preTestPersonalizationQ1Answer: {
    id: "patientEducation.faq.signatera.preTest.personalization.q1Answer",
    defaultMessage:
      "Signatera™ uses DNA from your own tumor tissue to design a personalized set of tumor-specific genetic markers. These markers are then tracked in your blood over time.",
  },
  preTestPersonalizationQ2Question: {
    id: "patientEducation.faq.signatera.preTest.personalization.q2Question",
    defaultMessage:
      "Is my Signatera™ test different from other patients' tests?",
  },
  preTestPersonalizationQ2Answer: {
    id: "patientEducation.faq.signatera.preTest.personalization.q2Answer",
    defaultMessage:
      "Yes. Each Signatera™ test is uniquely designed for one person and one specific cancer, based on the genetic makeup of that individual's tumor.",
  },

  // Pre-Test: Clinical Use
  preTestClinicalUseTitle: {
    id: "patientEducation.faq.signatera.preTest.clinicalUse.title",
    defaultMessage: "Clinical Use",
  },
  preTestClinicalUseQ1Question: {
    id: "patientEducation.faq.signatera.preTest.clinicalUse.q1Question",
    defaultMessage:
      "How will the Signatera™ test help my doctor manage my care?",
  },
  preTestClinicalUseQ1Answer: {
    id: "patientEducation.faq.signatera.preTest.clinicalUse.q1Answer",
    defaultMessage:
      "Signatera™ can help your doctor assess treatment effectiveness, detect residual disease earlier, and monitor for recurrence. Results are interpreted alongside imaging, physical exams, and other medical information.",
  },
  preTestClinicalUseQ2Question: {
    id: "patientEducation.faq.signatera.preTest.clinicalUse.q2Question",
    defaultMessage: "Will Signatera™ results be used on their own?",
  },
  preTestClinicalUseQ2Answer: {
    id: "patientEducation.faq.signatera.preTest.clinicalUse.q2Answer",
    defaultMessage:
      "No. Signatera™ does not replace scans or other tests and is intended to provide additional insight as part of your overall clinical evaluation.",
  },

  // Pre-Test: Timing
  preTestTimingTitle: {
    id: "patientEducation.faq.signatera.preTest.timing.title",
    defaultMessage: "Timing",
  },
  preTestTimingQ1Question: {
    id: "patientEducation.faq.signatera.preTest.timing.q1Question",
    defaultMessage: "When is the right time to have my first Signatera™ test?",
  },
  preTestTimingQ1Answer: {
    id: "patientEducation.faq.signatera.preTest.timing.q1Answer",
    defaultMessage:
      "Signatera™ testing is often done after surgery or completion of treatment to help establish a baseline for MRD monitoring. The exact timing depends on your care plan and is determined by your healthcare team.",
  },
  preTestTimingQ2Question: {
    id: "patientEducation.faq.signatera.preTest.timing.q2Question",
    defaultMessage: "Will I need repeated Signatera™ testing?",
  },
  preTestTimingQ2Answer: {
    id: "patientEducation.faq.signatera.preTest.timing.q2Answer",
    defaultMessage:
      "Many patients have repeat testing so their doctor can track changes in tumor DNA over time. Your doctor will recommend a testing schedule that fits your situation.",
  },

  // Pre-Test: Sample Requirements
  preTestSampleRequirementsTitle: {
    id: "patientEducation.faq.signatera.preTest.sampleRequirements.title",
    defaultMessage: "Sample Requirements",
  },
  preTestSampleRequirementsQ1Question: {
    id: "patientEducation.faq.signatera.preTest.sampleRequirements.q1Question",
    defaultMessage: "What samples are needed for Signatera™ testing?",
  },
  preTestSampleRequirementsQ1Answer: {
    id: "patientEducation.faq.signatera.preTest.sampleRequirements.q1Answer",
    defaultMessage:
      "A sample of your tumor tissue is used to design the personalized test, and blood samples are collected through a standard blood draw to look for circulating tumor DNA.",
  },
  preTestSampleRequirementsQ2Question: {
    id: "patientEducation.faq.signatera.preTest.sampleRequirements.q2Question",
    defaultMessage: "Will I need another tumor biopsy for future tests?",
  },
  preTestSampleRequirementsQ2Answer: {
    id: "patientEducation.faq.signatera.preTest.sampleRequirements.q2Answer",
    defaultMessage:
      "Usually, once the personalized test is created, ongoing monitoring only requires blood samples. Your doctor can confirm what applies to your care.",
  },

  // Pre-Test: Test Accuracy and Sensitivity
  preTestAccuracyTitle: {
    id: "patientEducation.faq.signatera.preTest.testAccuracy.title",
    defaultMessage: "Test Accuracy and Sensitivity",
  },
  preTestAccuracyQ1Question: {
    id: "patientEducation.faq.signatera.preTest.testAccuracy.q1Question",
    defaultMessage:
      "How accurate is Signatera™ at detecting minimal residual disease (MRD)?",
  },
  preTestAccuracyQ1Answer: {
    id: "patientEducation.faq.signatera.preTest.testAccuracy.q1Answer",
    defaultMessage:
      "Signatera™ is a highly personalized test designed to detect very low levels of tumor-specific DNA in the blood. However, no test is 100 percent accurate, and results are interpreted in clinical context.",
  },
  preTestAccuracyQ2Question: {
    id: "patientEducation.faq.signatera.preTest.testAccuracy.q2Question",
    defaultMessage: "Can Signatera™ miss cancer even if it is present?",
  },
  preTestAccuracyQ2Answer: {
    id: "patientEducation.faq.signatera.preTest.testAccuracy.q2Answer",
    defaultMessage:
      "Yes. Tumor DNA may be below the level of detection at certain times, which is why repeat testing and other monitoring tools are still important.",
  },

  // Pre-Test: Limitations
  preTestLimitationsTitle: {
    id: "patientEducation.faq.signatera.preTest.limitations.title",
    defaultMessage: "Limitations",
  },
  preTestLimitationsQ1Question: {
    id: "patientEducation.faq.signatera.preTest.limitations.q1Question",
    defaultMessage:
      "Are there situations where Signatera™ cannot provide a result?",
  },
  preTestLimitationsQ1Answer: {
    id: "patientEducation.faq.signatera.preTest.limitations.q1Answer",
    defaultMessage:
      "In some cases, the test cannot be completed due to sample quality issues or not enough tumor DNA. If this happens, your report will explain why, and repeat testing may be recommended.",
  },
  preTestLimitationsQ2Question: {
    id: "patientEducation.faq.signatera.preTest.limitations.q2Question",
    defaultMessage: "Can Signatera™ detect a new or different cancer?",
  },
  preTestLimitationsQ2Answer: {
    id: "patientEducation.faq.signatera.preTest.limitations.q2Answer",
    defaultMessage:
      "No. Signatera™ is customized to one specific cancer and cannot detect a new or unrelated primary cancer.",
  },

  // Pre-Test: Result Interpretation Expectations
  preTestResultInterpretationTitle: {
    id: "patientEducation.faq.signatera.preTest.resultInterpretation.title",
    defaultMessage: "Result Interpretation Expectations",
  },
  preTestResultInterpretationQ1Question: {
    id: "patientEducation.faq.signatera.preTest.resultInterpretation.q1Question",
    defaultMessage: "What type of results will I receive?",
  },
  preTestResultInterpretationQ1Answer: {
    id: "patientEducation.faq.signatera.preTest.resultInterpretation.q1Answer",
    defaultMessage:
      'Results are reported as "Positive (Detected)," "Negative (Not Detected)," or "Test Not Performed." Your doctor will review what the result means for your care.',
  },
  preTestResultInterpretationQ2Question: {
    id: "patientEducation.faq.signatera.preTest.resultInterpretation.q2Question",
    defaultMessage: "Should I review my results on my own?",
  },
  preTestResultInterpretationQ2Answer: {
    id: "patientEducation.faq.signatera.preTest.resultInterpretation.q2Answer",
    defaultMessage:
      "No. Signatera™ results should always be discussed with your healthcare provider, who will interpret them in the context of your overall health and treatment plan.",
  },

  // Pre-Test: Scope of Use
  preTestScopeOfUseTitle: {
    id: "patientEducation.faq.signatera.preTest.scopeOfUse.title",
    defaultMessage: "Scope of Use",
  },
  preTestScopeOfUseQ1Question: {
    id: "patientEducation.faq.signatera.preTest.scopeOfUse.q1Question",
    defaultMessage: "Can Signatera™ be used for all cancer types?",
  },
  preTestScopeOfUseQ1Answer: {
    id: "patientEducation.faq.signatera.preTest.scopeOfUse.q1Answer",
    defaultMessage:
      "Signatera™ is used for many solid tumor cancers, but not all cancer types are eligible. Your doctor can determine whether it is appropriate for your diagnosis.",
  },
  preTestScopeOfUseQ2Question: {
    id: "patientEducation.faq.signatera.preTest.scopeOfUse.q2Question",
    defaultMessage: "Can Signatera™ monitor more than one cancer?",
  },
  preTestScopeOfUseQ2Answer: {
    id: "patientEducation.faq.signatera.preTest.scopeOfUse.q2Answer",
    defaultMessage:
      "Yes. Signatera™ can be ordered separately to monitor more than one primary cancer when clinically appropriate, as determined by your healthcare provider.",
  },

  // Pre-Test: Follow-Up Planning
  preTestFollowUpPlanningTitle: {
    id: "patientEducation.faq.signatera.preTest.followUpPlanning.title",
    defaultMessage: "Follow-Up Planning",
  },
  preTestFollowUpPlanningQ1Question: {
    id: "patientEducation.faq.signatera.preTest.followUpPlanning.q1Question",
    defaultMessage:
      "What happens after I decide to proceed with Signatera™ testing?",
  },
  preTestFollowUpPlanningQ1Answer: {
    id: "patientEducation.faq.signatera.preTest.followUpPlanning.q1Answer",
    defaultMessage:
      "Your tumor tissue is used to design your personalized test, and a blood sample is collected. Results are sent to your doctor, who will discuss next steps with you.",
  },
  preTestFollowUpPlanningQ2Question: {
    id: "patientEducation.faq.signatera.preTest.followUpPlanning.q2Question",
    defaultMessage: "How will ongoing monitoring work?",
  },
  preTestFollowUpPlanningQ2Answer: {
    id: "patientEducation.faq.signatera.preTest.followUpPlanning.q2Answer",
    defaultMessage:
      "Your doctor may recommend repeat blood tests over time to track tumor DNA trends. These trends can help guide ongoing care and recurrence monitoring.",
  },

  // Post-Test: Result Meaning
  postTestResultMeaningTitle: {
    id: "patientEducation.faq.signatera.postTest.resultMeaning.title",
    defaultMessage: "Result Meaning",
  },
  postTestResultMeaningQ1Question: {
    id: "patientEducation.faq.signatera.postTest.resultMeaning.q1Question",
    defaultMessage:
      'What does my Signatera™ result mean — what\'s the difference between "Positive" and "Negative"?',
  },
  postTestResultMeaningQ1Answer: {
    id: "patientEducation.faq.signatera.postTest.resultMeaning.q1Answer",
    defaultMessage:
      'A "Positive" result means tumor DNA was detected in your blood, while a "Negative" result means no tumor DNA was detected at the time of testing. What this result means for your care is dependent on where you are in your treatment.',
  },
  postTestResultMeaningQ2Question: {
    id: "patientEducation.faq.signatera.postTest.resultMeaning.q2Question",
    defaultMessage: "Does a negative result mean my cancer is completely gone?",
  },
  postTestResultMeaningQ2Answer: {
    id: "patientEducation.faq.signatera.postTest.resultMeaning.q2Answer",
    defaultMessage:
      "No, a negative result means no tumor DNA was found at the time of the blood draw. However, this can be impacted by many clinical factors. While this can mean that there is no cancer DNA in your body, it can also mean it is at a level too low for detection. Your healthcare provider may still recommend follow-up scans and repeat testing.",
  },

  // Post-Test: Accuracy & Reliability
  postTestAccuracyReliabilityTitle: {
    id: "patientEducation.faq.signatera.postTest.accuracyReliability.title",
    defaultMessage: "Accuracy & Reliability",
  },
  postTestAccuracyReliabilityQ1Question: {
    id: "patientEducation.faq.signatera.postTest.accuracyReliability.q1Question",
    defaultMessage:
      "How accurate or reliable is my Signatera™ result? Could it be wrong?",
  },
  postTestAccuracyReliabilityQ1Answer: {
    id: "patientEducation.faq.signatera.postTest.accuracyReliability.q1Answer",
    defaultMessage:
      "Signatera™ is a highly personalized test designed to detect very low levels of tumor-specific DNA in the blood. However, sensitivity and specificity can vary based on tumor type and stage.",
  },
  postTestAccuracyReliabilityQ2Question: {
    id: "patientEducation.faq.signatera.postTest.accuracyReliability.q2Question",
    defaultMessage: "Can treatment or timing affect my result?",
  },
  postTestAccuracyReliabilityQ2Answer: {
    id: "patientEducation.faq.signatera.postTest.accuracyReliability.q2Answer",
    defaultMessage:
      "Yes. Tumor DNA levels can change based on treatment response or disease activity, which may affect whether tumor DNA is detected at a given time. This is why repeat testing is often recommended.",
  },

  // Post-Test: Quantitative Value Understanding
  postTestQuantitativeValueTitle: {
    id: "patientEducation.faq.signatera.postTest.quantitativeValue.title",
    defaultMessage: "Quantitative Value Understanding",
  },
  postTestQuantitativeValueQ1Question: {
    id: "patientEducation.faq.signatera.postTest.quantitativeValue.q1Question",
    defaultMessage: "What does the MTM/mL number on my report mean?",
  },
  postTestQuantitativeValueQ1Answer: {
    id: "patientEducation.faq.signatera.postTest.quantitativeValue.q1Answer",
    defaultMessage:
      "MTM/mL stands for mean tumor molecules per milliliter and shows how much tumor DNA was detected in your blood. It is mainly used to track changes over time rather than as a single stand-alone value.",
  },
  postTestQuantitativeValueQ2Question: {
    id: "patientEducation.faq.signatera.postTest.quantitativeValue.q2Question",
    defaultMessage: "What does it mean if my MTM/mL number goes up or down?",
  },
  postTestQuantitativeValueQ2Answer: {
    id: "patientEducation.faq.signatera.postTest.quantitativeValue.q2Answer",
    defaultMessage:
      "Changes in the MTM/mL number may reflect changes in tumor DNA levels in your blood. Your healthcare provider will look at trends over time to understand what this may mean for your care.",
  },

  // Post-Test: Result Change Over Time
  postTestResultChangeOverTimeTitle: {
    id: "patientEducation.faq.signatera.postTest.resultChangeOverTime.title",
    defaultMessage: "Result Change Over Time",
  },
  postTestResultChangeOverTimeQ1Question: {
    id: "patientEducation.faq.signatera.postTest.resultChangeOverTime.q1Question",
    defaultMessage:
      "Why might my Signatera™ result change from negative to positive?",
  },
  postTestResultChangeOverTimeQ1Answer: {
    id: "patientEducation.faq.signatera.postTest.resultChangeOverTime.q1Answer",
    defaultMessage:
      "Signatera may become positive if the amount of tumor DNA in your blood increases. This may mean you are at higher risk of recurrence. It is important to speak to your medical provider for what these results may mean for you.",
  },
  postTestResultChangeOverTimeQ2Question: {
    id: "patientEducation.faq.signatera.postTest.resultChangeOverTime.q2Question",
    defaultMessage: "Why might my result change from positive to negative?",
  },
  postTestResultChangeOverTimeQ2Answer: {
    id: "patientEducation.faq.signatera.postTest.resultChangeOverTime.q2Answer",
    defaultMessage:
      "Signatera become negative if treatment reduces or eliminates the tumor DNA. This can be a reassuring sign, but continued monitoring is still important because cancer behavior can change over time. Your healthcare provider will consider this change along with other clinical information.",
  },

  // Post-Test: Testing Frequency
  postTestTestingFrequencyTitle: {
    id: "patientEducation.faq.signatera.postTest.testingFrequency.title",
    defaultMessage: "Testing Frequency",
  },
  postTestTestingFrequencyQ1Question: {
    id: "patientEducation.faq.signatera.postTest.testingFrequency.q1Question",
    defaultMessage: "How often should I repeat the Signatera™ test?",
  },
  postTestTestingFrequencyQ1Answer: {
    id: "patientEducation.faq.signatera.postTest.testingFrequency.q1Answer",
    defaultMessage:
      "There is no single schedule that applies to every patient. The timing of repeat Signatera™ testing is individualized and depends on factors such as your cancer type, where you are in treatment or follow-up, and how your previous results have looked over time.",
  },
  postTestTestingFrequencyQ2Question: {
    id: "patientEducation.faq.signatera.postTest.testingFrequency.q2Question",
    defaultMessage: "Why is repeat testing important?",
  },
  postTestTestingFrequencyQ2Answer: {
    id: "patientEducation.faq.signatera.postTest.testingFrequency.q2Answer",
    defaultMessage:
      "A single result shows your status at one point in time, while repeat testing allows your healthcare provider to monitor changes. This helps support ongoing surveillance or treatment decisions.",
  },

  // Post-Test: Clinical Use
  postTestClinicalUseTitle: {
    id: "patientEducation.faq.signatera.postTest.clinicalUse.title",
    defaultMessage: "Clinical Use",
  },
  postTestClinicalUseQ1Question: {
    id: "patientEducation.faq.signatera.postTest.clinicalUse.q1Question",
    defaultMessage:
      "How will my healthcare provider use this result to guide my care?",
  },
  postTestClinicalUseQ1Answer: {
    id: "patientEducation.faq.signatera.postTest.clinicalUse.q1Answer",
    defaultMessage:
      "Your healthcare provider may use Signatera™ results to decide whether closer monitoring, additional tests, or treatment changes are needed. Results are always considered together with scans and exams.",
  },
  postTestClinicalUseQ2Question: {
    id: "patientEducation.faq.signatera.postTest.clinicalUse.q2Question",
    defaultMessage:
      "Will a positive result automatically mean I need treatment?",
  },
  postTestClinicalUseQ2Answer: {
    id: "patientEducation.faq.signatera.postTest.clinicalUse.q2Answer",
    defaultMessage:
      "No. A positive Signatera™ result does not automatically mean treatment will start or change. It means tumor DNA was detected in your blood at that time. A positive result is one piece of information, and your healthcare provider will consider it along with other findings before recommending next steps.",
  },

  // Post-Test: Limitations
  postTestLimitationsTitle: {
    id: "patientEducation.faq.signatera.postTest.limitations.title",
    defaultMessage: "Limitations",
  },
  postTestLimitationsQ1Question: {
    id: "patientEducation.faq.signatera.postTest.limitations.q1Question",
    defaultMessage: "Can Signatera™ detect a new or different type of cancer?",
  },
  postTestLimitationsQ1Answer: {
    id: "patientEducation.faq.signatera.postTest.limitations.q1Answer",
    defaultMessage:
      "No. Signatera™ can only detect DNA from the cancer it was designed for. It cannot identify a new or unrelated cancer because the genetic makeup would not match your personalized test.",
  },
  postTestLimitationsQ2Question: {
    id: "patientEducation.faq.signatera.postTest.limitations.q2Question",
    defaultMessage: "Can Signatera™ tell if my cancer is hereditary?",
  },
  postTestLimitationsQ2Answer: {
    id: "patientEducation.faq.signatera.postTest.limitations.q2Answer",
    defaultMessage:
      "No. Signatera™ looks only for tumor DNA and does not evaluate inherited genetic risk. Hereditary testing requires a different type of test.",
  },

  // Post-Test: Scope of Detection
  postTestScopeOfDetectionTitle: {
    id: "patientEducation.faq.signatera.postTest.scopeOfDetection.title",
    defaultMessage: "Scope of Detection",
  },
  postTestScopeOfDetectionQ1Question: {
    id: "patientEducation.faq.signatera.postTest.scopeOfDetection.q1Question",
    defaultMessage: "Can Signatera™ detect MRD in all cancer types?",
  },
  postTestScopeOfDetectionQ1Answer: {
    id: "patientEducation.faq.signatera.postTest.scopeOfDetection.q1Answer",
    defaultMessage:
      "Signatera™ is used for many solid tumor cancers, but not all cancer types are eligible. Your healthcare provider can confirm whether the test is appropriate for your diagnosis.",
  },
});

export const useFaqData = (): Partial<Record<TestType, FAQContent>> => {
  const intl = useIntl();

  return {
    [TestType.SIGNATERA]: {
      preTest: [
        {
          title: intl.formatMessage(messages.preTestPurposeTitle),
          content: [
            {
              question: intl.formatMessage(messages.preTestPurposeQ1Question),
              answer: intl.formatMessage(messages.preTestPurposeQ1Answer),
            },
            {
              question: intl.formatMessage(messages.preTestPurposeQ2Question),
              answer: intl.formatMessage(messages.preTestPurposeQ2Answer),
            },
          ],
        },
        {
          title: intl.formatMessage(messages.preTestPersonalizationTitle),
          content: [
            {
              question: intl.formatMessage(
                messages.preTestPersonalizationQ1Question
              ),
              answer: intl.formatMessage(
                messages.preTestPersonalizationQ1Answer
              ),
            },
            {
              question: intl.formatMessage(
                messages.preTestPersonalizationQ2Question
              ),
              answer: intl.formatMessage(
                messages.preTestPersonalizationQ2Answer
              ),
            },
          ],
        },
        {
          title: intl.formatMessage(messages.preTestClinicalUseTitle),
          content: [
            {
              question: intl.formatMessage(
                messages.preTestClinicalUseQ1Question
              ),
              answer: intl.formatMessage(messages.preTestClinicalUseQ1Answer),
            },
            {
              question: intl.formatMessage(
                messages.preTestClinicalUseQ2Question
              ),
              answer: intl.formatMessage(messages.preTestClinicalUseQ2Answer),
            },
          ],
        },
        {
          title: intl.formatMessage(messages.preTestTimingTitle),
          content: [
            {
              question: intl.formatMessage(messages.preTestTimingQ1Question),
              answer: intl.formatMessage(messages.preTestTimingQ1Answer),
            },
            {
              question: intl.formatMessage(messages.preTestTimingQ2Question),
              answer: intl.formatMessage(messages.preTestTimingQ2Answer),
            },
          ],
        },
        {
          title: intl.formatMessage(messages.preTestSampleRequirementsTitle),
          content: [
            {
              question: intl.formatMessage(
                messages.preTestSampleRequirementsQ1Question
              ),
              answer: intl.formatMessage(
                messages.preTestSampleRequirementsQ1Answer
              ),
            },
            {
              question: intl.formatMessage(
                messages.preTestSampleRequirementsQ2Question
              ),
              answer: intl.formatMessage(
                messages.preTestSampleRequirementsQ2Answer
              ),
            },
          ],
        },
        {
          title: intl.formatMessage(messages.preTestAccuracyTitle),
          content: [
            {
              question: intl.formatMessage(messages.preTestAccuracyQ1Question),
              answer: intl.formatMessage(messages.preTestAccuracyQ1Answer),
            },
            {
              question: intl.formatMessage(messages.preTestAccuracyQ2Question),
              answer: intl.formatMessage(messages.preTestAccuracyQ2Answer),
            },
          ],
        },
        {
          title: intl.formatMessage(messages.preTestLimitationsTitle),
          content: [
            {
              question: intl.formatMessage(
                messages.preTestLimitationsQ1Question
              ),
              answer: intl.formatMessage(messages.preTestLimitationsQ1Answer),
            },
            {
              question: intl.formatMessage(
                messages.preTestLimitationsQ2Question
              ),
              answer: intl.formatMessage(messages.preTestLimitationsQ2Answer),
            },
          ],
        },
        {
          title: intl.formatMessage(messages.preTestResultInterpretationTitle),
          content: [
            {
              question: intl.formatMessage(
                messages.preTestResultInterpretationQ1Question
              ),
              answer: intl.formatMessage(
                messages.preTestResultInterpretationQ1Answer
              ),
            },
            {
              question: intl.formatMessage(
                messages.preTestResultInterpretationQ2Question
              ),
              answer: intl.formatMessage(
                messages.preTestResultInterpretationQ2Answer
              ),
            },
          ],
        },
        {
          title: intl.formatMessage(messages.preTestScopeOfUseTitle),
          content: [
            {
              question: intl.formatMessage(
                messages.preTestScopeOfUseQ1Question
              ),
              answer: intl.formatMessage(messages.preTestScopeOfUseQ1Answer),
            },
            {
              question: intl.formatMessage(
                messages.preTestScopeOfUseQ2Question
              ),
              answer: intl.formatMessage(messages.preTestScopeOfUseQ2Answer),
            },
          ],
        },
        {
          title: intl.formatMessage(messages.preTestFollowUpPlanningTitle),
          content: [
            {
              question: intl.formatMessage(
                messages.preTestFollowUpPlanningQ1Question
              ),
              answer: intl.formatMessage(
                messages.preTestFollowUpPlanningQ1Answer
              ),
            },
            {
              question: intl.formatMessage(
                messages.preTestFollowUpPlanningQ2Question
              ),
              answer: intl.formatMessage(
                messages.preTestFollowUpPlanningQ2Answer
              ),
            },
          ],
        },
      ],
      postTest: [
        {
          title: intl.formatMessage(messages.postTestResultMeaningTitle),
          content: [
            {
              question: intl.formatMessage(
                messages.postTestResultMeaningQ1Question
              ),
              answer: intl.formatMessage(
                messages.postTestResultMeaningQ1Answer
              ),
            },
            {
              question: intl.formatMessage(
                messages.postTestResultMeaningQ2Question
              ),
              answer: intl.formatMessage(
                messages.postTestResultMeaningQ2Answer
              ),
            },
          ],
        },
        {
          title: intl.formatMessage(messages.postTestAccuracyReliabilityTitle),
          content: [
            {
              question: intl.formatMessage(
                messages.postTestAccuracyReliabilityQ1Question
              ),
              answer: intl.formatMessage(
                messages.postTestAccuracyReliabilityQ1Answer
              ),
            },
            {
              question: intl.formatMessage(
                messages.postTestAccuracyReliabilityQ2Question
              ),
              answer: intl.formatMessage(
                messages.postTestAccuracyReliabilityQ2Answer
              ),
            },
          ],
        },
        {
          title: intl.formatMessage(messages.postTestQuantitativeValueTitle),
          content: [
            {
              question: intl.formatMessage(
                messages.postTestQuantitativeValueQ1Question
              ),
              answer: intl.formatMessage(
                messages.postTestQuantitativeValueQ1Answer
              ),
            },
            {
              question: intl.formatMessage(
                messages.postTestQuantitativeValueQ2Question
              ),
              answer: intl.formatMessage(
                messages.postTestQuantitativeValueQ2Answer
              ),
            },
          ],
        },
        {
          title: intl.formatMessage(messages.postTestResultChangeOverTimeTitle),
          content: [
            {
              question: intl.formatMessage(
                messages.postTestResultChangeOverTimeQ1Question
              ),
              answer: intl.formatMessage(
                messages.postTestResultChangeOverTimeQ1Answer
              ),
            },
            {
              question: intl.formatMessage(
                messages.postTestResultChangeOverTimeQ2Question
              ),
              answer: intl.formatMessage(
                messages.postTestResultChangeOverTimeQ2Answer
              ),
            },
          ],
        },
        {
          title: intl.formatMessage(messages.postTestTestingFrequencyTitle),
          content: [
            {
              question: intl.formatMessage(
                messages.postTestTestingFrequencyQ1Question
              ),
              answer: intl.formatMessage(
                messages.postTestTestingFrequencyQ1Answer
              ),
            },
            {
              question: intl.formatMessage(
                messages.postTestTestingFrequencyQ2Question
              ),
              answer: intl.formatMessage(
                messages.postTestTestingFrequencyQ2Answer
              ),
            },
          ],
        },
        {
          title: intl.formatMessage(messages.postTestClinicalUseTitle),
          content: [
            {
              question: intl.formatMessage(
                messages.postTestClinicalUseQ1Question
              ),
              answer: intl.formatMessage(messages.postTestClinicalUseQ1Answer),
            },
            {
              question: intl.formatMessage(
                messages.postTestClinicalUseQ2Question
              ),
              answer: intl.formatMessage(messages.postTestClinicalUseQ2Answer),
            },
          ],
        },
        {
          title: intl.formatMessage(messages.postTestLimitationsTitle),
          content: [
            {
              question: intl.formatMessage(
                messages.postTestLimitationsQ1Question
              ),
              answer: intl.formatMessage(messages.postTestLimitationsQ1Answer),
            },
            {
              question: intl.formatMessage(
                messages.postTestLimitationsQ2Question
              ),
              answer: intl.formatMessage(messages.postTestLimitationsQ2Answer),
            },
          ],
        },
        {
          title: intl.formatMessage(messages.postTestScopeOfDetectionTitle),
          content: [
            {
              question: intl.formatMessage(
                messages.postTestScopeOfDetectionQ1Question
              ),
              answer: intl.formatMessage(
                messages.postTestScopeOfDetectionQ1Answer
              ),
            },
          ],
        },
      ],
    },
  };
};
